
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'baseui';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { LogIn } from 'lucide-react';
import { Spinner } from 'baseui/spinner';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useAd } from '../contexts/AdContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import tournamentService from '../services/tournamentService';
import { palettes } from '../constants/display/palettes';
import { LightTheme, DarkTheme } from 'baseui';
import {
  DisplayPageContainer,
  LoadingContainer,
  LoadingText,
  OverlayContainer,
  OverlayContent,
  OverlayHeader,
  OverlayInputOverrides,
  ErrorText
} from '../styles/displayPageStyles';
import DisplayHeader from '../components/display/DisplayHeader';
import DisplayStructure from '../components/display/DisplayStructure';
import { DisplaySettingsModal } from '../components/display/DisplaySettingsModal';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useDisplaySettings } from '../features/display/useDisplaySettings';
import { useConnectionLimiter } from '../features/display/useConnectionLimiter';
import { useDisplayData } from '../features/display/useDisplayData';
import { useDisplayTimer } from '../features/display/useDisplayTimer';
import { useDisplayResponsive } from '../features/display/useDisplayResponsive';
import { useDisplayWakeLock } from '../features/display/useDisplayWakeLock';
import { supabase } from '../services/supabase/supabase';

const DisplayPage = () => {
  const { tournamentId: urlTournamentId } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const { currentUser } = useAuth();
  const { updateAdVisibility } = useAd();
  const { config: appConfig } = useAppConfig();
  // eslint-disable-next-line no-unused-vars
  const { showNotification } = useNotification();
  const [tournamentIdInput, setTournamentIdInput] = useState(urlTournamentId || '');
  const [connectedTournamentId, setConnectedTournamentId] = useState(null);

  // Local display timer state
  const [connectionLimit, setConnectionLimit] = useState(10);

  useEffect(() => {
    if (urlTournamentId) {
      // Automatic connection logic if needed
    }
  }, [urlTournamentId]);

  // View & Responsive Logic
  const { isMobile, isPortrait, isFullscreen, currentTime, toggleFullScreen } = useDisplayResponsive();

  // Keep screen awake while displaying
  useDisplayWakeLock();

  // Use Custom Hooks
  const { tournamentData, isLoading: isDataLoading, error: dataError, fetchTournamentData, connectionStatus, getDebugLogs } = useDisplayData(connectedTournamentId, t, navigate);

  const displayRemainingTime = useDisplayTimer(tournamentData?.timer_state, () => fetchTournamentData(false));

  // Request persistent storage to prevent browser from auto-deleting IndexedDB
  useEffect(() => {
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then(persisted => {
        if (persisted) {
          console.log('Storage will be persisted and not cleared except by user action');
        } else {
          console.log('Storage may be cleared by the browser under storage pressure');
        }
      });
    }
  }, []);

  const handleConnectClick = async () => {
    if (!tournamentIdInput.trim()) return;
    // Simple validation before setting connected ID
    const { data, error: invokeError } = await supabase.functions.invoke('validate-tournament-id', {
      body: { tournamentId: tournamentIdInput.trim() },
    });

    if (invokeError || !data?.isValid) {
      // Local error handling for the input
      return;
    }

    setConnectedTournamentId(tournamentIdInput.trim());
    if (urlTournamentId !== tournamentIdInput.trim()) {
      navigate(`/t/${tournamentIdInput.trim()}`);
    }
  };

  // Logic to handle loading/error state locally for the initial connection attempt is tricky 
  // because useDisplayData handles the actual fetching.
  // We can rely on useDisplayData's error/loading.
  // But handleConnectClick also does a pre-check.
  // We'll trust useDisplayData for the main state.

  // Extract raw values
  const timerState = tournamentData?.timer_state?.state;
  const rowIndex = tournamentData?.timer_state?.rowIndex ?? 0;
  const structure = tournamentData?.structure || { columns: [], levels: [], events: [] };
  const tournamentDetails = tournamentData?.details;
  const tournamentName = tournamentData?.name;


  useEffect(() => {
    const fetchOwnerTier = async () => {
      if (tournamentData && tournamentData.owner_id) {
        const { data, error } = await tournamentService.getUserTier(tournamentData.owner_id);
        if (error) {
          updateAdVisibility(currentUser, 'free');
          setConnectionLimit(10);
        } else {
          const tier = data?.tier;
          updateAdVisibility(currentUser, tier);
          // Free tier: 10, Paid tiers: 100
          setConnectionLimit(tier === 'free' ? 10 : 100);
        }
      } else {
        updateAdVisibility(currentUser, 'free');
        setConnectionLimit(10);
      }
    };
    fetchOwnerTier();
  }, [currentUser, tournamentData, updateAdVisibility]);

  // Connection Limiting Logic
  const { isLimitExceeded, currentCount, limit } = useConnectionLimiter(tournamentData?.owner_id, connectionLimit);

  const {
    settings,
    currentThemeName,
    actions: settingsActions
  } = useDisplaySettings(
    structure, rowIndex, displayRemainingTime, timerState, null, null, true, true
  );

  const palette = palettes[currentThemeName] || palettes.midnight;
  const { themeMode } = useTheme();
  const baseTheme = themeMode === 'dark' ? DarkTheme : LightTheme;
  const customTheme = {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...palette,
    },
  };

  // Sound settings state (still local as it controls a modal)
  const [isSoundSettingsOpen, setIsSoundSettingsOpen] = useState(false);

  if (!connectedTournamentId) {
    return (
      <OverlayContainer $palette={palette}>
        <OverlayContent>
          <OverlayHeader $palette={palette}>{t('display.connectToTournament')}</OverlayHeader>
          <Input
            value={tournamentIdInput}
            onChange={e => setTournamentIdInput(e.target.value)}
            placeholder={t('display.enterTournamentIdPlaceholder')}
            overrides={OverlayInputOverrides}
            onKeyDown={e => e.key === 'Enter' && handleConnectClick()}
            disabled={isDataLoading}
            autoFocus
          />
          {dataError && <ErrorText>{dataError}</ErrorText>}
          <Button
            onClick={handleConnectClick}
            startEnhancer={() => <LogIn size={24} />}
            disabled={!tournamentIdInput.trim() || isDataLoading}
            isLoading={isDataLoading}
          >
            {t('display.connectButton')}
          </Button>
        </OverlayContent>
      </OverlayContainer>
    );
  }

  if (isDataLoading || !tournamentData) {
    return (
      <LoadingContainer $palette={palette}>
        <Spinner />
        <LoadingText $palette={palette}>{t('common.loading')}</LoadingText>
      </LoadingContainer>
    );
  }

  if (isLimitExceeded) {
    return (
      <OverlayContainer $palette={palette} style={{ backgroundColor: 'black' }}>
        <OverlayContent>
          <ErrorText style={{ fontSize: '18px', marginBottom: '20px' }}>
            {t('errors.connectionLimitExceeded')}
          </ErrorText>
          <div style={{ color: 'white', textAlign: 'center' }}>
            <p>{t('errors.connectionLimitDetails', { current: currentCount, limit: limit })}</p>
            <p>{t('errors.connectionLimitAdvice')}</p>
          </div>
        </OverlayContent>
      </OverlayContainer>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <DisplayPageContainer $palette={palette}>
        <DisplayHeader
          palette={palette}
          structure={structure}
          tournamentDetails={tournamentDetails}
          tournamentName={tournamentName}
          remainingTime={displayRemainingTime}
          rowIndex={rowIndex}
          currentTime={currentTime}
          isFullscreen={isFullscreen}
          isPortrait={isPortrait}
          showQrCode={settings.showQrCode}
          toggleFullScreen={toggleFullScreen}
          onOpenSettings={() => { settingsActions.getAudioContext(); setIsSoundSettingsOpen(true); }}
          onToggleQrCode={settingsActions.toggleQrCode}
          t={t}
        />

        <DisplayStructure
          palette={palette}
          structure={structure}
          rowIndex={rowIndex}
          isMobilePortrait={isMobile && isPortrait}
          t={t}
          appConfig={appConfig}
        />

        <DisplaySettingsModal
          isOpen={isSoundSettingsOpen}
          onClose={() => setIsSoundSettingsOpen(false)}
          palette={palette}
          t={t}
          locale={locale}
          isMobile={isMobile}
          isPortrait={isPortrait}
          settings={settings}
          currentThemeName={currentThemeName}
          structure={structure}
          actions={settingsActions}
        />

        {/* コネクション状態インジケータ（極小） */}
        <div
          onClick={async () => {
            try {
              const logs = getDebugLogs();
              await navigator.clipboard.writeText(logs.join('\n'));
              showNotification(t('display.debugLogsCopied') || 'Debug logs copied to clipboard', 'info');
              // Optionally log to console for mobile devices without easy clipboard inspection
              console.log(logs.join('\n'));
            } catch (ignored) {}
          }}
          title={`Status: ${connectionStatus} (Click to copy logs)`}
          style={{
            position: 'fixed',
            bottom: '4px',
            right: '4px',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: connectionStatus === 'SUBSCRIBED' ? 'lightgreen' : (connectionStatus === 'INIT' ? 'gray' : '#ff9900'),
            cursor: 'pointer',
            zIndex: 9999,
            opacity: 0.5
          }}
        />
      </DisplayPageContainer>
    </ThemeProvider>
  );
};

export default DisplayPage;